import React, { useState, useRef } from 'react';
import {ref, uploadBytes, getDownloadURL} from "firebase/storage";
import { submitUserQuestion, updateUserQuestionImage } from '../Helpers/firebaseHelpers';
import {storage} from "../firebase-config";

const QuestionSubmission = () =>
{
    const [formData, setFormData] = useState({
        questionName: '',
        questionStatement: '',
        optionA: '',
        optionB: '',
        optionC: '',
        optionD: '',
        CorrectAnswer: '',
        solution: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [questionImageFile, setQuestionImageFile] = useState(null);
    const fileInputRef = useRef(null);

    const resetForm = () =>
    {
        setFormData({
            questionName: '',
            questionStatement: '',
            optionA: '',
            optionB: '',
            optionC: '',
            optionD: '',
            CorrectAnswer: '',
            solution: ''
        });
        setQuestionImageFile(null);
        if (fileInputRef.current)
        {
            fileInputRef.current.value = '';
        }
    };

    // Check if all required fields are filled
    const isFormValid = () =>
    {
        return formData.questionName.trim() !== '' &&
               formData.questionStatement.trim() !== '' &&
               formData.optionA.trim() !== '' &&
               formData.optionB.trim() !== '' &&
               formData.optionC.trim() !== '' &&
               formData.optionD.trim() !== '' &&
               formData.CorrectAnswer !== '';
    };

    const handleInputChange = (field, value) =>
    {
        setFormData({
            ...formData,
            [field]: value
        });
    };

    const uploadQuestionImage = async (file, docId) =>
    {
        if (!file)
        {
            return null;
        }

        const allowedTypes = 
        [
            "image/jpeg",
            "image/png",
        ]

        if (!allowedTypes.includes(file.type))
        {
            alert(`Unsupported error type: ${file.type}`);
            return null;
        }

        const MAX_SIZE = 8 * 1024 *1024;  // 8 MB

        if (file.size > MAX_SIZE)
        {
            alert("Image should be less than 8 MB");
            return null;
        }

        const imageRef = ref(
            storage,
            `User_Proposed_Q_Images/${docId}`
        );

        try 
        {
            await uploadBytes(imageRef, file, { contentType: file.type });
            return await getDownloadURL(imageRef);
        } catch (err) 
        {
            console.error(err);
            return null;
        }
    }

    const handleSubmit = async () =>
    {
        if (!isFormValid())
        {
            return;
        }
                
        setIsSubmitting(true);
        
        try
        {
            const result = await submitUserQuestion(formData);

            const docId = result.id;
            let image_URL = null;
            
            if (result.success && questionImageFile)
            {
                image_URL = await uploadQuestionImage(questionImageFile, docId);
                await updateUserQuestionImage(docId, image_URL);

                resetForm();
                alert("✅ Question with the attached image submitted successfully! We'll review it soon.");
            } else if (result.success)
            {
                resetForm();
                alert("✅ Question submitted successfully! We'll review it soon.");
            }
            else
            {
                alert("❌ Failed to submit question: " + result.error);
            }
        } catch (error)
        {
            alert("❌ An error occurred while submitting the question.");
        }
        
        setIsSubmitting(false);
    };

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="mb-6">
                <h2 className="text-3xl font-bold mb-2">Submit a Question</h2>
                <p className="text-gray-600">Help us grow our question bank by submitting your own questions!</p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
                {/* Question Name */}
                <div className="mb-6">
                    <label className="block text-lg font-semibold mb-2 text-red-600">
                        Question Name *
                    </label>
                    <input
                        type="text"
                        value={formData.questionName}
                        onChange={(e) => handleInputChange('questionName', e.target.value)}
                        placeholder="Enter a short name for your question..."
                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                    />
                </div>

                {/* Question Statement */}
                <div className="mb-6">
                    <label className="block text-lg font-semibold mb-2 text-red-600">
                        Question Statement *
                    </label>
                    <textarea
                        value={formData.questionStatement}
                        onChange={(e) => handleInputChange('questionStatement', e.target.value)}
                        placeholder="Enter your question here..."
                        className="w-full p-4 border border-gray-300 rounded-lg h-32 resize-none focus:border-blue-500 focus:outline-none"
                    />
                </div>

                <div className="mt-4 mb-4">
                    <label className="block text-red-600 text-lg font-medium mb-1">
                        Question Image <span className='text-blue-600'>(optional)</span>
                    </label>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => setQuestionImageFile(e.target.files[0] || null)}
                    />

                    {questionImageFile && (
                        <p className="text-md text-green-700 mt-1">
                        Selected: {questionImageFile.name}
                        </p>
                    )}
                </div>

                {/* Options */}
                <div className="mb-6">
                    <label className="block text-lg font-semibold mb-4 text-red-600">
                        Options *
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {['A', 'B', 'C', 'D'].map((option) => (
                            <div key={option}>
                                <label className="block text-sm font-medium mb-2">
                                    Option {option}
                                </label>
                                <input
                                    type="text"
                                    value={formData[`option${option}`]}
                                    onChange={(e) => handleInputChange(`option${option}`, e.target.value)}
                                    placeholder={`Enter option ${option}`}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Correct Answer */}
                <div className="mb-6">
                    <label className="block text-lg font-semibold mb-2 text-red-600">
                        Correct Answer *
                    </label>
                    <select
                        value={formData.CorrectAnswer}
                        onChange={(e) => handleInputChange('CorrectAnswer', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                    >
                        <option value="">Select correct answer</option>
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                        <option value="D">D</option>
                    </select>
                </div>

                {/* Solution (Optional) */}
                <div className="mb-6">
                    <label className="block text-lg font-semibold mb-2">
                        Solution (Optional)
                    </label>
                    <textarea
                        value={formData.solution}
                        onChange={(e) => handleInputChange('solution', e.target.value)}
                        placeholder="Explain the solution (optional)..."
                        className="w-full p-4 border border-gray-300 rounded-lg h-24 resize-none focus:border-blue-500 focus:outline-none"
                    />
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                    <button
                        onClick={handleSubmit}
                        disabled={!isFormValid() || isSubmitting}
                        className={`px-8 py-3 rounded-lg text-lg font-medium transition-all ${
                            isFormValid() && !isSubmitting
                                ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Question'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QuestionSubmission;
